import { useMemo } from 'react'
import { AGENT_LABELS } from '../lib/agents.js'
import KitchenLoader from './KitchenLoader.jsx'
import AgentRanBadge from './AgentRanBadge.jsx'
import { useScheduleSuggestions } from '../hooks/useScheduleSuggestions.js'
import { useScheduleData } from '../hooks/useScheduleData.js'

// ScheduleSuggestionsSection — AI per-day staffing tweaks for the next
// 7 days, mounted on the Schedule hub above the sub-screens.
//
// READ-ONLY. The list is for the manager to act on manually. There is
// NO "apply this suggestion" affordance — no write path to
// schedule_assignments lives in this component, in the hook, or in the
// edge function. If a future ship wants apply-in-place, it needs its
// own plan + pre-mortem.
//
// State machine (single render path at the bottom, no early returns):
//   idle       — no cache row within 6h; show Generate prompt
//   generating — edge fn in flight; show KitchenLoader
//   cached     — payload from the 6h cache window; show cards
//   ready      — fresh generate() succeeded; show cards
//   empty      — payload._empty (tiny-data nudge OR timeout retryable)
//   error      — calm deterministic fallback per CLAUDE.md
//
// Hook ordering (CLAUDE.md error #310): all hooks fire BEFORE any
// conditional return. The render branch is a switch at the bottom.

const LOADER_MESSAGES = [
  'Looking at coverage…',
  'Cross-checking sales by day…',
  'Drafting your suggestions…',
]

// Action badge palette — paired bg / text classes that read distinctly
// at a glance. Keep these tight; the manager scans many at once.
const ACTION_BADGE = {
  add:  { bg: 'bg-green-50',   text: 'text-green-700',   label: 'Add' },
  trim: { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'Trim' },
  move: { bg: 'bg-blue-50',    text: 'text-blue-700',    label: 'Move' },
  keep: { bg: 'bg-neutral-100', text: 'text-neutral-600', label: 'Keep' },
}

// Health pill palette.
const HEALTH_PILL = {
  good:      { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Coverage looks good' },
  attention: { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'Needs attention' },
  concern:   { bg: 'bg-rose-50',    text: 'text-rose-700',    label: 'Coverage concern' },
}

function formatDateLabel(isoDate, dayName) {
  if (!isoDate) return dayName || ''
  // Render as "Mon May 26". UTC parse to avoid the off-by-one from
  // local-tz interpretation of a YYYY-MM-DD string.
  const d = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return dayName || isoDate
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
  const day   = d.getUTCDate()
  const dow   = dayName || d.toLocaleString('en-US', { weekday: 'short', timeZone: 'UTC' })
  return `${dow} ${month} ${day}`
}

function formatCooldown(ms) {
  const s = Math.ceil(ms / 1000)
  return `Wait ${s}s`
}

export default function ScheduleSuggestionsSection() {
  // HOOKS FIRST (CLAUDE.md error #310). No conditional returns above this block.
  const { phase, payload, generatedAt, cooldownRemainingMs, generate, error } =
    useScheduleSuggestions()

  // Pull staff for client-side name resolution. The hook hits the
  // current week of schedule_assignments + active staff with RLS. We
  // only need the staff list here; weekStart is fine as today.
  // (suggested_staff_id was already server-validated against the
  // snapshot.staff[] list, so it's guaranteed to exist; but the staff
  // list shape on the edge response is intentionally minimal, so we
  // resolve names client-side from the live staff table.)
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const { activeStaff } = useScheduleData(todayIso)

  const staffById = useMemo(() => {
    const map = new Map()
    for (const s of activeStaff || []) {
      if (s?.id) map.set(s.id, s)
    }
    return map
  }, [activeStaff])

  const suggestions = Array.isArray(payload?.suggestions) ? payload.suggestions : []
  const overallHealth = payload?.overall_health || null
  const summary = payload?.summary || null

  const cooldownActive = cooldownRemainingMs > 0
  const generating = phase === 'generating'
  const buttonDisabled = generating || cooldownActive
  const buttonLabel = generating
    ? 'Generating…'
    : cooldownActive
      ? formatCooldown(cooldownRemainingMs)
      : phase === 'cached' || phase === 'ready' || phase === 'empty'
        ? 'Regenerate'
        : 'Generate suggestions'

  const showCardsState = (phase === 'ready' || phase === 'cached') && suggestions.length >= 0
  const showEmpty = phase === 'empty'
  const showError = phase === 'error'
  const showIdle = phase === 'idle'

  // SINGLE render path. Branches are inline-conditional, not early returns.
  return (
    <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-[11px] font-medium text-black/50">
            {AGENT_LABELS.scheduleSuggestions}
          </div>
          <h3 className="mt-0.5 text-sm font-semibold text-black/85">
            Per-day staffing tweaks for the next 7 days
          </h3>
        </div>
        <div className="flex items-baseline gap-2">
          <AgentRanBadge generatedAt={generatedAt} tone="light" />
          <button
            type="button"
            onClick={() => generate(false)}
            disabled={buttonDisabled}
            className={[
              'rounded-md px-2.5 py-1 text-xs font-medium',
              buttonDisabled
                ? 'cursor-not-allowed bg-black/5 text-black/40'
                : 'bg-black text-white hover:bg-black/85',
            ].join(' ')}
          >
            {buttonLabel}
          </button>
        </div>
      </div>

      {/* IDLE — no cache, no auto-fire. Prompt the user. */}
      {showIdle && (
        <p className="mt-3 text-sm text-black/65">
          Tap Generate suggestions to scan the next 7 days for coverage gaps,
          overstaffed shifts, and busy days worth flagging. Read-only suggestions —
          you stay in control of the schedule.
        </p>
      )}

      {/* GENERATING — KitchenLoader (NEVER the generic blue spinner). */}
      {generating && (
        <div className="mt-3">
          <KitchenLoader
            messages={LOADER_MESSAGES}
            intervalMs={1800}
            size="md"
            align="col"
          />
        </div>
      )}

      {/* EMPTY — tiny-data nudge OR retryable timeout payload from edge fn. */}
      {showEmpty && (
        <div className="mt-3 rounded-xl bg-black/5 p-3 text-sm text-black/70">
          {payload?.headline || "We couldn't analyze enough data yet."}
          {payload?.narrative && (
            <div className="mt-1 text-xs text-black/55">{payload.narrative}</div>
          )}
        </div>
      )}

      {/* ERROR — calm fallback, NEVER raw error text. */}
      {showError && (
        <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50/60 p-3 text-sm text-rose-800">
          {error || "We couldn't pull suggestions right now."}
          <div className="mt-1 text-xs text-rose-700/80">
            Tap Generate suggestions in a moment to try again.
          </div>
        </div>
      )}

      {/* READY / CACHED — summary, health pill, and the cards. */}
      {showCardsState && (
        <>
          {(summary || overallHealth) && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {overallHealth && HEALTH_PILL[overallHealth] && (
                <span
                  className={[
                    'rounded-full px-2 py-0.5 text-[11px] font-medium',
                    HEALTH_PILL[overallHealth].bg,
                    HEALTH_PILL[overallHealth].text,
                  ].join(' ')}
                >
                  {HEALTH_PILL[overallHealth].label}
                </span>
              )}
              {summary && (
                <p className="text-sm text-black/75">{summary}</p>
              )}
            </div>
          )}

          {suggestions.length === 0 ? (
            <div className="mt-3 rounded-xl bg-emerald-50/60 p-3 text-sm text-emerald-900">
              Nothing to tweak — the next 7 days look balanced against recent demand.
            </div>
          ) : (
            <ul className="mt-3 space-y-2">
              {suggestions.map((s, idx) => {
                const badge = ACTION_BADGE[s.action] || ACTION_BADGE.keep
                const dateLabel = formatDateLabel(s.shift_date, s.day_name)
                const staff = s.suggested_staff_id ? staffById.get(s.suggested_staff_id) : null
                const timeWindow =
                  s.suggested_start && s.suggested_end
                    ? `${s.suggested_start}–${s.suggested_end}`
                    : null
                return (
                  <li
                    key={`${s.shift_date}-${s.action}-${s.role}-${idx}`}
                    className="rounded-xl border border-black/10 bg-white p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-semibold text-black/75">
                        {dateLabel}
                      </div>
                      <span
                        className={[
                          'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                          badge.bg,
                          badge.text,
                        ].join(' ')}
                      >
                        {badge.label}
                      </span>
                    </div>

                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-black/85">
                      <span className="font-medium capitalize">{s.role}</span>
                      {timeWindow && (
                        <span className="text-xs text-black/55">{timeWindow}</span>
                      )}
                      {staff && (
                        <span className="rounded-md bg-black/5 px-1.5 py-0.5 text-[11px] text-black/65">
                          {staff.name}
                        </span>
                      )}
                    </div>

                    <p className="mt-1 break-words text-xs text-black/65">
                      {s.rationale}
                    </p>
                  </li>
                )
              })}
            </ul>
          )}

          {generatedAt && (
            <div className="mt-2 text-[10px] text-black/40">
              Generated {new Date(generatedAt).toLocaleString()}
            </div>
          )}
        </>
      )}
    </section>
  )
}
